import { useState, useEffect, useCallback } from 'react';
import { getStoredToken, getStoredUser, logoutUser, refreshUserFromDB, isAuthValid, type CustomUser } from '@/lib/customAuth';

interface AuthState {
  user: CustomUser | null;
  loading: boolean;
  refresh: () => void;
}

export const useAuth = (): AuthState => {
  const [user, setUser] = useState<CustomUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    // 检查登录态是否有效（7天记住我 or session）
    if (!isAuthValid()) {
      logoutUser();
      setUser(null);
      setLoading(false);
      return;
    }
    // 直接从数据库拉最新数据，不用缓存
    const fresh = await refreshUserFromDB();
    if (!fresh) {
      // token 存在但数据库查不到，清除登录态
      logoutUser();
      setUser(null);
    } else {
      setUser(fresh);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 监听登出事件
  useEffect(() => {
    const handler = () => { setUser(null); };
    window.addEventListener('custom-auth-change', handler);
    return () => window.removeEventListener('custom-auth-change', handler);
  }, []);

  return { user, loading, refresh };
};

export const signOut = (): void => {
  logoutUser();
  window.dispatchEvent(new Event('custom-auth-change'));
};
